import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useEvent } from 'react-use-event-hook'
import { Avatar } from '@components/Avatar/Avatar'
import { useUploadAttachment } from '@components/MediaDropContext/useUploadAttachment'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { MutualTowns } from '@components/MutualTowns/MutualTowns'
import { useMutualTowns } from '@components/MutualTowns/useMutualTowns'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { PanelButton } from '@components/Panel/PanelButton'
import {
    SetUsernameDisplayName,
    useCurrentStreamID,
} from '@components/SetUsernameDisplayName/SetUsernameDisplayName'
import { Spinner } from '@components/Spinner'
import { TipCounter } from '@components/TipCounter/TipCounter'
import { LargeUploadImageTemplate } from '@components/UploadImage/LargeUploadImageTemplate'
import { UploadImageRequestConfig } from '@components/UploadImage/useOnImageChangeEvent'
import { UserBadges } from '@components/UserBadges/UserBadges'
import { UserRoles } from '@components/UserRoles/UserRoles'
import { VerifiedOnChainAssetTooltip } from '@components/VerifiedOnChainAssetTooltip/VerifiedOnChainAssetTooltip'
import { useAppRenewCountdownData } from '@components/Web3/RenewApp/hooks/useAppRenewCountdownData'
import { RenewBotInfoAlert } from '@components/Web3/RenewApp/RenewBotInfoAlert'
import { TestnetTownsWalletDisplay } from '@components/Web3/Wallet/TownsWallet'
import { isDefined, isDMChannelStreamId, Nft, spaceIdFromChannelId } from '@towns-protocol/sdk'
import {
    Box,
    Button,
    Divider,
    FormRender,
    Icon,
    IconButton,
    MotionIcon,
    Paragraph,
    Stack,
    Text,
    TextButton,
} from '@ui'
import { AnimatePresence, MotionConfig } from 'framer-motion'
import {
    // this is the one place in the app you can see another user's abstract account address
    // eslint-disable-next-line
    useAbstractAccountAddress,
    useMyAbstractAccountAddress,
} from 'hooks/useAbstractAccountAddress'
import { useDevice } from 'hooks/useDevice'
import { useNfts, useResolveNft } from 'hooks/useNfts'
import { useSetNftProfilePicture } from 'hooks/useSetNftProfilePicture'
import { useSetUserBio } from 'hooks/useUserBio'
import { type GetSigner, WalletReady } from 'privy/WalletReady'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { TextArea } from 'ui/components/TextArea/TextArea'
import {
    SpaceAddressFromSpaceId,
    SpaceIdFromSpaceAddress,
    TransactionStatus,
    useBotData,
    useBotStatus,
    useIsSpaceOwner,
    useUninstallApp,
    useUserLookup,
} from 'use-towns-client'
import { env } from 'utils'
import { Address } from 'viem'

import { AboutTheBot } from './AboutTheBot'
import { BotAuthorizeUpdateDialog } from './BotAuthorizeUpdateDialog'
import { BotReviews } from './BotReviews'
import { UserWalletContent } from './UserWalletContent'

type Props = {
    displayName: string
    center?: boolean
    info?: { title: string; content?: string | React.JSX.Element }[]
    userBio?: string
    userId?: string
    canEdit?: boolean
}

enum InputId {
    Bio = 'Bio',
}

export const UserProfile = (props: Props) => {
    const { userId, canEdit, center, info, userBio } = props
    const user = useUserLookup(userId ?? '')
    const { hasMutualTowns } = useMutualTowns(userId)
    const [showNftProfilePicture, setShowNftProfilePicture] = useState(false)
    const [isRemovingNft, setIsRemovingNft] = useState(false)
    const { openPanel } = usePanelActions()
    const { data: abstractAccountAddress } = useAbstractAccountAddress({
        rootKeyAddress: userId as Address | undefined,
    })

    const { setNft } = useSetNftProfilePicture()
    const streamId = useCurrentStreamID()
    const spaceAddress = useMemo(() => {
        try {
            const spaceId = spaceIdFromChannelId(streamId ?? '')
            return SpaceAddressFromSpaceId(spaceId) as Address
        } catch (error) {
            return undefined
        }
    }, [streamId])
    const { needsUpdate: botNeedsUpdate } = useBotNeedsUpdate({
        userId,
        appAddress: user?.appAddress,
    })
    const { data: bot } = useBotData(user?.appAddress)
    const { data: myAbstractAccountAddress } = useMyAbstractAccountAddress()

    const isDMContext = useMemo(() => isDMChannelStreamId(streamId ?? ''), [streamId])
    const { isOwner: currentUserIsSpaceOwner } = useIsSpaceOwner(
        spaceAddress,
        myAbstractAccountAddress,
    )
    const onClearNft = useCallback(
        (event: React.MouseEvent) => {
            if (!streamId) {
                return
            }
            event.stopPropagation()
            event.preventDefault()
            setIsRemovingNft(true)
            setNft(streamId, '', 0, '')
        },
        [setNft, streamId],
    )
    const resolvedNft = useResolveNft({ walletAddress: userId ?? '', info: user?.nft })
    const { mutateAsync: mutateAsyncBio } = useSetUserBio(userId)

    const hasNftProfilePicture = useMemo(() => !!user?.nft, [user?.nft])

    useEffect(() => {
        if (!hasNftProfilePicture) {
            setIsRemovingNft(false)
        }
    }, [hasNftProfilePicture])

    const nftUrl = useMemo(() => {
        if (!resolvedNft) {
            return
        }

        const image = resolvedNft.image
        if (image && image.thumbnailUrl) {
            return image.thumbnailUrl
        }

        if (image && image.originalUrl) {
            return image.originalUrl
        }
    }, [resolvedNft])

    const resourceId = useMemo(() => {
        if (!userId) {
            return ''
        }
        if (nftUrl) {
            return userId + '_' + nftUrl
        }
        return userId
    }, [userId, nftUrl])

    const { uploadUserProfileImageToStream } = useUploadAttachment()
    const isUploadingUserProfileImageRef = useRef<boolean>(false)
    const onUploadUserProfileImage = useCallback(
        async ({ file, id: userId, type, setProgress }: UploadImageRequestConfig) => {
            if (isUploadingUserProfileImageRef.current || !userId) {
                return
            }
            if (type === 'avatar') {
                isUploadingUserProfileImageRef.current = true
                try {
                    await uploadUserProfileImageToStream(userId, file, setProgress)
                } finally {
                    isUploadingUserProfileImageRef.current = false
                }
            }
        },
        [uploadUserProfileImageToStream],
    )

    const onSaveItem = useEvent((id: string, content: undefined | string) => {
        switch (id) {
            case InputId.Bio: {
                if (!abstractAccountAddress) {
                    throw new Error('no user address provided')
                }

                content = content?.trim() ?? ''

                return mutateAsyncBio(content)
            }
        }
    })

    const onWriteBotReviewClick = useCallback(() => {
        const appAddress = user?.appAddress
        if (!appAddress) {
            return
        }
        openPanel(CHANNEL_INFO_PARAMS.BOT_REVIEW, { profileId: appAddress })
    }, [openPanel, user?.appAddress])

    return (
        <Stack grow gap paddingBottom="none" position="relative">
            <Stack centerContent={center}>
                <FormRender maxWidth="200" width="100%" key={resourceId}>
                    {({ register, formState, setError, clearErrors }) => (
                        <LargeUploadImageTemplate
                            canEdit={!!canEdit}
                            type="avatar"
                            formFieldName="avatar"
                            resourceId={resourceId}
                            setError={setError}
                            register={register}
                            formState={formState}
                            clearErrors={clearErrors}
                            imageRestrictions={{
                                minDimension: {
                                    message: `Image is too small. Please upload an image with a minimum height & width of 300px.`,
                                    min: 300,
                                },
                            }}
                            dataTestId="user-avatar-upload"
                            onUploadImage={onUploadUserProfileImage}
                        >
                            <Stack centerContent aspectRatio="1/1">
                                <Avatar
                                    userId={userId}
                                    size="avatar_x20"
                                    imageVariant="thumbnail300"
                                    isBot={user?.isBot}
                                />
                                <AnimatePresence mode="wait" initial={false}>
                                    {botNeedsUpdate &&
                                        currentUserIsSpaceOwner &&
                                        user?.appAddress &&
                                        spaceAddress &&
                                        userId &&
                                        !isDMContext && (
                                            <Box
                                                position="absolute"
                                                style={{
                                                    bottom: '-4px',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                }}
                                            >
                                                <UpdateBot
                                                    userId={userId}
                                                    appAddress={user?.appAddress}
                                                    spaceAddress={spaceAddress}
                                                />
                                            </Box>
                                        )}
                                </AnimatePresence>
                            </Stack>
                        </LargeUploadImageTemplate>
                    )}
                </FormRender>
                {(resolvedNft || canEdit) && (
                    <Stack
                        horizontal
                        grow
                        hoverable
                        paddingX
                        paddingY="sm"
                        gap="sm"
                        rounded="full"
                        background="level2"
                        width="100%"
                        alignItems="center"
                        cursor={canEdit ? 'pointer' : 'default'}
                        tooltip={<VerifiedOnChainAssetTooltip userId={userId} />}
                        onClick={() => {
                            if (canEdit) {
                                setShowNftProfilePicture(true)
                            }
                        }}
                    >
                        <Icon type="verifiedEnsName" size="square_sm" />
                        {resolvedNft ? (
                            <>
                                <Text size="sm" fontWeight="medium">
                                    {resolvedNft.title}
                                </Text>
                            </>
                        ) : (
                            <Text fontWeight="medium" fontSize="sm">
                                Add NFT Profile Picture
                            </Text>
                        )}

                        <Box grow />

                        {canEdit && hasNftProfilePicture ? (
                            isRemovingNft ? (
                                <Box padding="xs">
                                    <ButtonSpinner square="square_xs" />
                                </Box>
                            ) : (
                                <IconButton
                                    icon="close"
                                    size="square_xs"
                                    color="default"
                                    onClick={onClearNft}
                                />
                            )
                        ) : (
                            <></>
                        )}
                    </Stack>
                )}
            </Stack>
            {user?.isBot &&
                user?.appAddress &&
                spaceAddress &&
                currentUserIsSpaceOwner &&
                !isDMContext && (
                    <BotRenewalSection
                        spaceId={SpaceIdFromSpaceAddress(spaceAddress)}
                        appAddress={user.appAddress}
                    />
                )}

            {/* I want to see this in non-omega envs when i don't have any towns joined */}
            {env.DEV && !canEdit && userId === abstractAccountAddress && (
                <TestnetTownsWalletDisplay />
            )}

            {canEdit ? (
                <>
                    <TestnetTownsWalletDisplay />
                    {streamId && <SetUsernameDisplayName streamId={streamId} />}
                </>
            ) : (
                <Stack padding gap="paragraph" rounded="sm" background="level2">
                    {user && userId && (
                        <>
                            {user.displayName.length > 0 && (
                                <Stack horizontal gap="xs" alignItems="center" height="x1.5">
                                    <Text color="default" fontSize="lg" fontWeight="strong">
                                        {user.displayName}
                                    </Text>
                                    {user.isBot && (
                                        <Icon
                                            type="robot"
                                            size="square_xs"
                                            color="gray2"
                                            insetTop="1"
                                        />
                                    )}
                                    <TipCounter userId={userId} />
                                </Stack>
                            )}
                            {user.username?.length > 1 && (
                                <Stack horizontal gap="xxs" alignItems="center">
                                    {user.isBot ? (
                                        <Text color="gray2">@{user.username}</Text>
                                    ) : (
                                        <Text color="default">
                                            {user.displayName ? '' : '@'}
                                            {user.username}
                                        </Text>
                                    )}
                                    {!user.displayName && <TipCounter userId={userId} />}
                                </Stack>
                            )}
                        </>
                    )}
                    <UserWalletContent
                        abstractAccountAddress={user.appAddress ?? abstractAccountAddress}
                    />
                </Stack>
            )}

            {/* If user is a bot, show a warning if the bot is deactivated */}
            {user?.appAddress && <BotDeactivatedWarning botClientAddress={userId} />}

            {userId && user?.isBot && <AboutTheBot bot={bot} />}

            {hasMutualTowns && (
                <Stack
                    horizontal
                    gap
                    paddingY="sm"
                    paddingX="lg"
                    background="level2"
                    rounded="sm"
                    alignItems="center"
                >
                    <MutualTowns userId={userId} />
                </Stack>
            )}

            {userId && user?.isBot && (
                <BotReviews
                    userId={userId}
                    botAppAddress={user?.appAddress}
                    onClick={onWriteBotReviewClick}
                />
            )}

            {(canEdit || (userBio && userBio !== '')) && (
                <Stack grow gap>
                    <EditModeContainer
                        inputId={InputId.Bio}
                        initialValue={userBio}
                        canEdit={canEdit}
                        onSave={onSaveItem}
                    >
                        {({
                            editMenu,
                            value,
                            isEditing,
                            isSaving,
                            error,
                            errorComponent,
                            onChange,
                            handleEdit,
                        }) => {
                            return (
                                <Stack grow padding gap background="level2" rounded="sm">
                                    <Stack horizontal>
                                        <Stack
                                            gap
                                            grow
                                            onClick={canEdit && !isEditing ? handleEdit : undefined}
                                        >
                                            <Paragraph strong color="default">
                                                {user?.isBot ? 'About the Bot' : 'Bio'}
                                            </Paragraph>
                                        </Stack>
                                        <Box>{editMenu}</Box>
                                    </Stack>
                                    <Stack gap="sm">
                                        {!isEditing ? (
                                            <Paragraph color="gray2">
                                                {userBio ?? `no biography just yet`}
                                            </Paragraph>
                                        ) : (
                                            <TextArea
                                                autoFocus
                                                key={error ? 'errored' : 'not-errored'}
                                                tone={error ? 'error' : undefined}
                                                paddingY="md"
                                                background="level2"
                                                defaultValue={value}
                                                height="100"
                                                maxLength={160}
                                                disabled={isSaving}
                                                onChange={onChange}
                                            />
                                        )}
                                        {errorComponent}
                                    </Stack>
                                </Stack>
                            )
                        }}
                    </EditModeContainer>
                    {showNftProfilePicture && userId && (
                        <NftProfilePicture
                            userId={userId}
                            streamId={streamId}
                            currentNft={user?.nft}
                            onHide={() => setShowNftProfilePicture(false)}
                        />
                    )}
                    {!!info?.length &&
                        info.map((n) => (
                            <>
                                <Paragraph strong>{n.title}</Paragraph>
                                <Paragraph color="gray2">{n.content}</Paragraph>
                            </>
                        ))}
                </Stack>
            )}
            <UserRoles userId={userId} />
            {userId && <UserBadges userId={userId} />}
        </Stack>
    )
}

type EditRowProps = {
    inputId: string
    initialValue: string | undefined
    children: (renderOptions: EditRowRenderProps) => React.JSX.Element
    onSave: (id: string, content: undefined | string) => Promise<void> | void
    canEdit?: boolean
}

type EditRowRenderProps = {
    editMenu: React.JSX.Element | undefined
    value: string | undefined
    isEditing: boolean
    isSaving: boolean
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    handleSave: () => void
    handleEdit: () => void
    error: Error | undefined
    errorComponent: React.JSX.Element | undefined
}

// TODO: refactor
export const EditModeContainer = (props: EditRowProps) => {
    const [value, setValue] = useState<string | undefined>(props.initialValue)
    const [isEditing, setIsEditing] = React.useState(false)
    const [error, setError] = useState<Error | undefined>()

    const handleEdit = useEvent(() => {
        setIsEditing((e) => !e)
    })

    const [isSaving, setIsSaving] = useState(false)

    const handleSave = useEvent(() => {
        const save = async () => {
            setIsSaving(true)
            try {
                await props.onSave(props.inputId, value)
                setIsEditing(false)
            } catch (e: unknown) {
                console.error('error while saving UserProfile', e)
                setError(e as Error)
            }
            setIsSaving(false)
        }

        save()
    })

    useEffect(() => {
        if (!isEditing) {
            setError(undefined)
            setValue(props.initialValue)
        }
    }, [isEditing, props.initialValue])

    useEffect(() => {
        setError(undefined)
        // value is a needed dependency
    }, [value])

    const onChange = useEvent((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setValue(e.target.value)
    })

    const editMenu = useMemo(
        () =>
            !props.canEdit ? undefined : (
                <Box horizontal gap="sm" shrink={false}>
                    {isSaving ? (
                        <Stack horizontal gap="sm" alignItems="center" color="positive">
                            <Paragraph size="sm">Updating</Paragraph>
                            <Spinner square="square_xxs" />
                        </Stack>
                    ) : (
                        <>
                            <TextButton onClick={handleEdit}>
                                {!isEditing ? 'Edit' : 'Cancel'}
                            </TextButton>
                            {isEditing && (
                                <TextButton color="default" onClick={handleSave}>
                                    Save
                                </TextButton>
                            )}
                        </>
                    )}
                </Box>
            ),
        [handleEdit, handleSave, isEditing, isSaving, props.canEdit],
    )

    const errorComponent = useMemo(() => {
        return error ? (
            <Box paddingY="sm">
                <Paragraph color="error" size="sm">
                    {error?.message}
                </Paragraph>
            </Box>
        ) : undefined
    }, [error])

    return (
        <>
            <Stack
                horizontal
                justifyContent="spaceBetween"
                alignItems="baseline"
                position="relative"
            >
                {props.children({
                    editMenu,
                    isEditing,
                    isSaving,
                    onChange,
                    handleSave,
                    handleEdit,
                    errorComponent,
                    error,
                    value,
                })}
            </Stack>
        </>
    )
}

const NftProfilePicture = (props: {
    onHide: () => void
    userId: string
    streamId: string | undefined
    currentNft?: Nft
}) => {
    const { onHide, userId, streamId, currentNft } = props

    const { isTouch } = useDevice()
    const { nfts, isFetching } = useNfts({ walletAddress: userId })
    const { setNft } = useSetNftProfilePicture()
    const { openPanel } = usePanelActions()
    const [selectedNft, setSelectedNft] = useState<Nft | undefined>(currentNft)
    const [pendingSaveNft, setPendingSaveNft] = useState<Nft | undefined>(undefined)

    const isNftSaved = useMemo(
        () =>
            currentNft?.contractAddress &&
            currentNft?.contractAddress == pendingSaveNft?.contractAddress,
        [currentNft?.contractAddress, pendingSaveNft?.contractAddress],
    )

    useEffect(() => {
        if (isNftSaved) {
            setPendingSaveNft(undefined)
            onHide()
        }
    }, [isNftSaved, onHide, pendingSaveNft])

    const displayableNfts = useMemo(() => {
        if (!nfts) {
            return []
        }
        return nfts
            .filter(isDefined)
            .filter((nft) => isDefined(nft.data.image?.cachedUrl))
            .filter((nft) => isDefined(nft.data.displayNft?.tokenId))
            .map((nft) => {
                return {
                    chainId: nft.chainId,
                    image: nft.data.image,
                    contractAddress: nft.data.address,
                    tokenId: nft.data.displayNft?.tokenId ?? '',
                } as const
            })
    }, [nfts])

    const saveNft = useCallback(() => {
        if (!selectedNft || !streamId) {
            return
        }
        setNft(streamId, selectedNft.tokenId, selectedNft.chainId, selectedNft.contractAddress)
        setPendingSaveNft(selectedNft)
    }, [selectedNft, streamId, setNft])

    const saveInProgress = !!pendingSaveNft
    const saveButtonEnabled =
        !saveInProgress && !isNftSaved && selectedNft?.tokenId != currentNft?.tokenId

    const onViewLinkedWalletsClick = useCallback(() => {
        onHide()
        openPanel(CHANNEL_INFO_PARAMS.WALLETS)
    }, [onHide, openPanel])

    const hasNfts = displayableNfts.length > 0 && !isFetching

    return (
        <ModalContainer asSheet maxWidth="600" onHide={onHide}>
            {!isTouch && (
                <Box position="relative">
                    <IconButton position="topRight" icon="close" onClick={onHide} />
                </Box>
            )}
            <Stack gap paddingTop={isTouch ? 'md' : 'lg'}>
                <Stack gap alignItems="center">
                    <Text size="lg" fontWeight="strong" color="default">
                        NFT Profile Picture
                    </Text>
                </Stack>

                {isFetching && (
                    <Box padding>
                        <ButtonSpinner />
                    </Box>
                )}
                {!hasNfts && (
                    <Box grow centerContent padding>
                        <Text textAlign="center" color="gray2">
                            No NFTs were found in your wallets.
                            <br /> Link a new wallet to set a verified NFT as your profile picture.
                        </Text>
                    </Box>
                )}
                <Stack
                    horizontal
                    centerContent
                    scrollbars
                    scroll
                    gap
                    flexWrap="wrap"
                    width="100%"
                    maxHeight={isTouch ? '50vh' : '300'}
                >
                    {displayableNfts.map((nft) => (
                        <Box
                            hoverable
                            key={nft.contractAddress + nft.tokenId + nft.chainId}
                            position="relative"
                            width="x12"
                            height="x12"
                            background="level2"
                            rounded="xs"
                            border={{
                                default:
                                    nft.tokenId == selectedNft?.tokenId &&
                                    nft.contractAddress == selectedNft.contractAddress
                                        ? 'positive'
                                        : 'none',
                                hover: 'positive',
                            }}
                            style={{
                                backgroundImage: `url(${nft.image?.cachedUrl})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                            onClick={() => setSelectedNft(nft)}
                        >
                            <AnimatePresence>
                                <MotionConfig transition={{ ease: 'easeOut', duration: 0.2 }}>
                                    {nft.tokenId == selectedNft?.tokenId &&
                                        nft.contractAddress == selectedNft.contractAddress && (
                                            <>
                                                <MotionIcon
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                    zIndex="uiAbove"
                                                    square="square_xxs"
                                                    position="absolute"
                                                    top="xxs"
                                                    right="xxs"
                                                    type="verifiedEnsName"
                                                    style={{
                                                        strokeWidth: 1,
                                                        stroke: 'black',
                                                    }}
                                                />
                                                <MotionIcon
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                    square="square_xxs"
                                                    position="absolute"
                                                    top="xxs"
                                                    right="xxs"
                                                    type="verifiedEnsName"
                                                    style={{
                                                        stroke: 'black',
                                                        strokeWidth: 2,
                                                        filter: 'brightness(0%)',
                                                    }}
                                                />
                                            </>
                                        )}
                                </MotionConfig>
                            </AnimatePresence>
                        </Box>
                    ))}
                </Stack>
                <Stack gap shrink={false}>
                    {hasNfts && (
                        <>
                            <Button
                                tone={saveButtonEnabled ? 'cta1' : 'level2'}
                                width="100%"
                                disabled={!saveButtonEnabled}
                                onClick={saveNft}
                            >
                                {pendingSaveNft ? <ButtonSpinner /> : 'Set as Profile Picture'}
                            </Button>
                            <Stack horizontal gap="sm">
                                <Divider />
                                <Text shrink={false} color="gray2" fontSize="sm">
                                    Add more verified NFT profile pictures:
                                </Text>
                                <Divider />
                            </Stack>
                        </>
                    )}
                    <Button
                        tone={hasNfts ? 'level2' : 'cta1'}
                        width="100%"
                        onClick={onViewLinkedWalletsClick}
                    >
                        Link a Wallet
                    </Button>
                </Stack>
            </Stack>
        </ModalContainer>
    )
}

const BotDeactivatedWarning = ({ botClientAddress }: { botClientAddress: string | undefined }) => {
    const { data: botStatus } = useBotStatus(botClientAddress as `0x${string}`)
    if (botStatus?.isActive !== false) {
        return null
    }
    return (
        <Stack
            horizontal
            gap
            padding="md"
            background="negativeSubtle"
            rounded="md"
            alignItems="center"
        >
            <Icon type="alert" size="square_sm" color="error" />
            <Paragraph color="default">This bot has been deactivated.</Paragraph>
        </Stack>
    )
}

const useBotNeedsUpdate = (props: {
    userId: string | undefined
    appAddress: Address | undefined
}) => {
    const { userId, appAddress } = props
    const botUserLookup = useUserLookup(userId ?? '')
    const { data: bot } = useBotData(appAddress)
    const needsUpdate = useMemo(() => {
        const lookupAppId = botUserLookup?.appId
        if (!bot?.latestAppId || !lookupAppId) {
            return false
        }
        return bot.latestAppId != lookupAppId
    }, [bot?.latestAppId, botUserLookup?.appId])

    return { needsUpdate }
}

const UpdateBot = (props: { userId: string; appAddress: Address; spaceAddress: Address }) => {
    const { appAddress, spaceAddress } = props
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const { data: bot } = useBotData(appAddress)

    return (
        <>
            <Button
                tone="purpleGradient"
                color="default"
                rounded="lg"
                onClick={() => setIsDialogOpen(true)}
            >
                Update
            </Button>
            <BotAuthorizeUpdateDialog
                isOpen={isDialogOpen}
                bot={bot}
                appAddress={appAddress}
                spaceAddress={spaceAddress}
                onClose={() => setIsDialogOpen(false)}
            />
        </>
    )
}

const BotRenewalSection = (props: { spaceId: string; appAddress: Address }) => {
    const { spaceId, appAddress } = props
    const { isExpired, isExpiringSoon, isLoading } = useAppRenewCountdownData({
        spaceId,
        appAddress,
    })

    if (isLoading) {
        return null
    }
    if (isExpired || isExpiringSoon) {
        return <RenewBotInfoAlert cta spaceId={spaceId} appAddress={appAddress} />
    }
    return null
}

export const UninstallBotButton = (props: {
    appAddress: Address
    walletAddress: Address
    installPrice?: bigint
}) => {
    const { appAddress, walletAddress, installPrice } = props
    const [showConfirmation, setShowConfirmation] = useState(false)
    const { execute: uninstallApp, isLoading, transactionStatus } = useUninstallApp()

    useEffect(() => {
        if (transactionStatus === TransactionStatus.Success) {
            popupToast(({ toast }) => (
                <StandardToast.Success
                    toast={toast}
                    message="Bot uninstalled successfully!"
                    subMessage="The bot has been removed from your account."
                />
            ))
        }
        if (transactionStatus === TransactionStatus.Failed) {
            popupToast(({ toast }) => (
                <StandardToast.Error toast={toast} message="Failed to uninstall bot" />
            ))
        }
    }, [transactionStatus])

    const handleUninstall = useCallback(
        async (getSigner: GetSigner) => {
            const signer = await getSigner()
            if (!signer) {
                createPrivyNotAuthenticatedNotification()
                return
            }

            await uninstallApp({
                target: 'userAccount',
                userAddress: walletAddress,
                appAddress,
                signer,
            })
        },
        [appAddress, walletAddress, uninstallApp],
    )

    return (
        <>
            <PanelButton tone="negative" onClick={() => setShowConfirmation(true)}>
                <Icon type="delete" size="square_sm" />
                <Paragraph>Uninstall Bot</Paragraph>
            </PanelButton>

            {showConfirmation && (
                <ModalContainer
                    padding="none"
                    disableBackgroundClick={isLoading}
                    onHide={() => !isLoading && setShowConfirmation(false)}
                >
                    <Stack gap="sm">
                        <Stack
                            horizontal
                            alignItems="center"
                            justifyContent="center"
                            padding="lg"
                            position="relative"
                        >
                            <Text fontWeight="strong" color="default">
                                Uninstall Bot
                            </Text>
                            <IconButton
                                position="absolute"
                                style={{ right: '16px' }}
                                icon="close"
                                size="square_sm"
                                disabled={isLoading}
                                onClick={() => !isLoading && setShowConfirmation(false)}
                            />
                        </Stack>
                    </Stack>
                    <Box padding="lg">
                        <Stack gap="sm">
                            <Text color="gray2">
                                Are you sure you want to uninstall this bot from your account?
                            </Text>
                            {installPrice !== undefined && installPrice > 0n && (
                                <Text color="gray2" fontSize="sm">
                                    If you uninstall, you will need to pay again to reinstall it.
                                </Text>
                            )}
                        </Stack>
                    </Box>
                    <Box padding="lg" paddingTop="md" borderTop="default">
                        <Stack horizontal justifyContent="end" gap="sm">
                            <Button
                                tone="level2"
                                size="button_md"
                                disabled={isLoading}
                                onClick={() => !isLoading && setShowConfirmation(false)}
                            >
                                Cancel
                            </Button>
                            <WalletReady>
                                {({ getSigner }) => (
                                    <Button
                                        tone="negative"
                                        size="button_md"
                                        disabled={isLoading}
                                        onClick={() => handleUninstall(getSigner)}
                                    >
                                        {isLoading ? <ButtonSpinner /> : 'Uninstall'}
                                    </Button>
                                )}
                            </WalletReady>
                        </Stack>
                    </Box>
                </ModalContainer>
            )}
        </>
    )
}
